import { Controller } from "@hotwired/stimulus"

// CloudFront経由でfeed.jsonをfetchし、instagram-feedセクションのDOMを実行時に組み立てる。
// 生成するDOM構造・クラス名は既存home.html.erbのERB出力と同一にし、instagram_touch_controller.js（既存・無改修）を
// そのまま流用できるようにしている。
export default class extends Controller {
  static values = {
    cloudfrontUrl: String // data-instagram-feed-cloudfront-url-value から注入される（末尾スラッシュなし）
  }

  connect() {
    // データ取得が完了するまでセクション自体を隠しておく（0件・エラー時は表示しない）
    this.element.hidden = true
    this.abortController = new AbortController()
    this.load()
  }

  disconnect() {
    // 描画完了前にDOMから外れた場合に備えてfetchを中断する
    this.abortController.abort()
  }

  async load() {
    let data
    try {
      const response = await fetch(`${this.cloudfrontUrlValue}/feed/data/feed.json`, {
        signal: this.abortController.signal
      })
      if (!response.ok) return

      data = await response.json()
    } catch {
      // fetch失敗・abort・JSON parse失敗はすべて非表示のまま終了させる
      return
    }

    // yohei/scenevibesが配列でない不正なレスポンスは表示しない
    if (!Array.isArray(data?.yohei) || !Array.isArray(data?.scenevibes)) return

    // 表示件数は既存仕様どおり yohei は先頭1件、scenevibes は先頭4件のみ
    const yohei = data.yohei[0]
    const scenevibes = data.scenevibes.slice(0, 4)
    // 両方とも投稿が無ければ表示するものが無いため非表示のまま終了（片方のみでも表示する）
    if (!yohei && !scenevibes[0]) return

    this.render(yohei, scenevibes)
    this.element.hidden = false
  }

  render(yohei, scenevibes) {
    this.element.replaceChildren()

    if (yohei) {
      this.element.appendChild(this.buildHeroLink(yohei))
    }

    if (scenevibes.length > 0) {
      const grid = document.createElement("div")
      grid.className = "instagram-feed__grid"
      scenevibes.forEach(post => grid.appendChild(this.buildGridItem(post)))
      this.element.appendChild(grid)
    }
  }

  // yoheiの投稿1件をヒーローリンク（画像+キャプション付きオーバーレイ）として組み立てる
  buildHeroLink(post) {
    const link = document.createElement("a")
    link.className = "instagram-feed__link"
    link.href = post.permalink
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    link.setAttribute("data-action", "click->instagram-touch#activate")

    const imageWrap = document.createElement("div")
    imageWrap.className = "instagram-feed__image-wrap"

    const image = document.createElement("img")
    image.className = "instagram-feed__image"
    image.src = post.image_url
    image.alt = ""
    image.loading = "lazy"

    const overlay = document.createElement("div")
    overlay.className = "instagram-feed__overlay"

    const icon = this.buildInstagramIcon("instagram-feed__icon")

    const label = document.createElement("span")
    label.className = "instagram-feed__label"
    label.textContent = `${this.truncateCaption(post.caption)}…`

    overlay.appendChild(icon)
    overlay.appendChild(label)
    imageWrap.appendChild(image)
    imageWrap.appendChild(overlay)
    link.appendChild(imageWrap)

    return link
  }

  // scenevibesの投稿1件をグリッドアイテム（画像のみ、キャプションなし）として組み立てる
  buildGridItem(post) {
    const link = document.createElement("a")
    link.className = "instagram-feed__grid-item"
    link.href = post.permalink
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    link.setAttribute("data-action", "click->instagram-touch#activate")

    const image = document.createElement("img")
    image.src = post.image_url
    image.alt = ""
    image.loading = "lazy"

    const icon = this.buildInstagramIcon("instagram-feed__grid-icon")

    link.appendChild(image)
    link.appendChild(icon)

    return link
  }

  // Font Awesomeの全量CSS読み込みを避けるため、instagramアイコンをインラインSVGで組み立てる
  buildInstagramIcon(className) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("class", className)
    svg.setAttribute("viewBox", "0 0 448 512")
    svg.setAttribute("aria-hidden", "true")
    svg.setAttribute("focusable", "false")

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
    path.setAttribute("fill", "currentColor")
    path.setAttribute("d", "M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z")

    svg.appendChild(path)
    return svg
  }

  // 既存ERBの caption&.split("\n\n")&.first(6)&.join("\n") と同じ挙動をJSで再現する
  // （呼び出し側で末尾に「…」を付与するため、captionがnullでも空文字を返す）
  truncateCaption(caption) {
    return (caption ?? "").split("\n\n").slice(0, 6).join("\n")
  }
}
